import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { odataGet, odataEscape } from './dataverse';

/**
 * Compose role + capability model. Follows design-system/ACCESS-ROLES-PATTERN.md
 * Piece 3 — per-app role table (gaqc_composerole), per-capability gating,
 * no-lockout legacy fallback.
 *
 * Roles for v1 (per STAGE-0-SCOPE.md §"Where it lives"):
 *   user    — compose & manage own drafts
 *   auditor — read-only access to others' drafts for QA
 *   admin   — manage the template library + per-app roles
 */

export type ComposeRole = 'user' | 'auditor' | 'admin';

export type Capability =
  | 'compose' // create/edit SODAN drafts
  | 'review' // read others' drafts (QA)
  | 'editLibrary' // edit templates / D&N / add-ons
  | 'manageUsers'; // assign Compose roles

export const ROLE_CAPS: Record<ComposeRole, Record<Capability, boolean>> = {
  user: { compose: true, review: false, editLibrary: false, manageUsers: false },
  auditor: { compose: false, review: true, editLibrary: false, manageUsers: false },
  admin: { compose: true, review: true, editLibrary: true, manageUsers: true },
};

export const CAPABILITIES: Capability[] = ['compose', 'review', 'editLibrary', 'manageUsers'];

/**
 * Owner break-glass — always admin regardless of the role table. Same
 * convention as Pipeline/i-broker. Prevents an empty role table from
 * locking everyone out on deploy day.
 */
export const OWNER_EMAIL = 'a.price@getaquoteltd.co.uk';

/** Current signed-in user, derived from the MSAL active account. */
export function useCurrentUser(): { upn: string; displayName: string } | null {
  const { accounts } = useMsal();
  const account = accounts[0];
  if (!account) return null;
  return {
    upn: account.username.toLowerCase(),
    displayName: account.name ?? account.username,
  };
}

/**
 * The signed-in user's effective Compose role.
 *
 * Source of truth: their gaqc_composerole row (keyed by UPN lowercased —
 * the CRITICAL CONTRACT from the access-roles pattern). Falls back to the
 * owner break-glass, then to 'user'. Fails safe to 'user' on any error so
 * we never hand admin tools to the wrong person.
 */
export function useComposeRole(): { role: ComposeRole; loading: boolean } {
  const user = useCurrentUser();
  const q = useQuery({
    queryKey: ['compose.role', user?.upn],
    queryFn: async (): Promise<ComposeRole> => {
      if (!user) return 'user';
      if (user.upn === OWNER_EMAIL) return 'admin';
      try {
        const r = await odataGet<{ gaqc_role?: string }>(
          'gaqc_composeroles',
          `?$filter=gaqc_name eq '${odataEscape(user.upn)}'&$select=gaqc_role&$top=1`
        );
        const role = r.value[0]?.gaqc_role;
        if (role === 'user' || role === 'auditor' || role === 'admin') return role;
        return 'user';
      } catch {
        return 'user';
      }
    },
    enabled: !!user,
    staleTime: 10 * 60 * 1000,
  });
  return { role: q.data ?? 'user', loading: q.isLoading };
}

/** Convenience: does the current user have a given capability? */
export function useCan(cap: Capability): boolean {
  const { role } = useComposeRole();
  return ROLE_CAPS[role][cap] === true;
}

/**
 * UPN → display name map from the shared gaqh_teammembers table (also
 * drives Holiday Tracker / i-broker / Pipeline). Falls open: returns the
 * raw UPN if a name isn't found or the read fails.
 */
export function useTeamDisplayName(): (upn: string | null | undefined) => string {
  const q = useQuery({
    queryKey: ['compose.teamDisplayNames'],
    queryFn: async (): Promise<Record<string, string>> => {
      const r = await odataGet<{ gaqh_email?: string; gaqh_displayname?: string }>(
        'gaqh_teammembers',
        '?$select=gaqh_email,gaqh_displayname&$top=200'
      );
      const map: Record<string, string> = {};
      for (const m of r.value ?? []) {
        const upn = (m.gaqh_email ?? '').toLowerCase();
        const name = (m.gaqh_displayname ?? '').trim();
        if (upn && name) map[upn] = name;
      }
      return map;
    },
    staleTime: 30 * 60 * 1000,
    retry: 2,
  });

  return useMemo(() => {
    const map = q.data ?? {};
    return (upn) => {
      if (!upn) return '';
      return map[upn.toLowerCase()] ?? upn;
    };
  }, [q.data]);
}
